/**
 * Authentication Utilities
 * Centralized authentication checking and user management utilities
 */

/**
 * Get user ID from user object with multiple fallback patterns
 * @param {Object} user - User object from Redux store
 * @returns {string|null} - User ID or null if not authenticated
 */
export const getUserId = (user) => {
  if (!user) return null;
  
  // Try different user object patterns
  return (
    user?.user?.id ||           // Firebase Auth pattern
    user?.user?.uid ||          // Firebase UID pattern
    user?.uid ||                // Direct UID pattern
    user?.id ||                 // Direct ID pattern
    user?.user?.docId ||        // Document ID pattern
    null
  );
};

/**
 * Get user email from user object with multiple fallback patterns
 * @param {Object} user - User object from Redux store
 * @returns {string|null} - User email or null if not authenticated
 */
export const getUserEmail = (user) => {
  if (!user) return null;
  
  return (
    user?.user?.email ||        // Firebase Auth pattern
    user?.email ||              // Direct email pattern
    null
  );
};

/**
 * Check if user is authenticated
 * @param {Object} user - User object from Redux store
 * @returns {boolean} - True if user is authenticated
 */
export const isAuthenticated = (user) => {
  const userId = getUserId(user);
  const userEmail = getUserEmail(user);
  
  return !!(userId && userEmail);
};

/**
 * Check if user is admin
 * @param {Object} user - User object from Redux store
 * @returns {boolean} - True if user is admin
 */
export const isAdmin = (user) => {
  const userEmail = getUserEmail(user);
  if (!userEmail) return false;
  
  const adminEmails = [
    process.env.REACT_APP_ADMIN,
    process.env.REACT_APP_ARI_ADMIN
  ].filter(Boolean);
  
  return adminEmails.includes(userEmail) || user?.user?.isAdmin || false;
};

/**
 * Get user display name with fallbacks
 * @param {Object} user - User object from Redux store
 * @returns {string} - User display name or default
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Utilizator';
  
  return (
    user?.user?.displayName ||
    user?.user?.name ||
    user?.displayName ||
    user?.name ||
    user?.user?.firstName ||
    user?.firstName ||
    getUserEmail(user) ||
    'Utilizator'
  );
};

/**
 * Get user rank with fallback
 * @param {Object} user - User object from Redux store
 * @returns {number} - User rank (1-5)
 */
export const getUserRank = (user) => {
  if (!user) return 1;
  
  return (
    user?.user?.rank ||
    user?.rank ||
    1
  );
};

/**
 * Show authentication required message
 * @param {string} action - Action that requires authentication
 * @param {Function} toast - Toast notification function
 */
export const showAuthRequiredMessage = (action = 'această acțiune', toast) => {
  if (toast) {
    toast.error(`🔐 Trebuie să fii autentificat pentru ${action}`);
  }
};

/**
 * Create consistent user data object for API calls
 * @param {Object} user - User object from Redux store
 * @returns {Object|null} - Sanitized user data or null
 */
export const createUserDataForAPI = (user) => {
  const userId = getUserId(user);
  const userEmail = getUserEmail(user);
  
  if (!userId || !userEmail) return null;
  
  return {
    userId,
    email: userEmail,
    displayName: getUserDisplayName(user),
    rank: getUserRank(user)
  };
};

/**
 * Validate user authentication before API call
 * @param {Object} user - User object from Redux store
 * @param {string} action - Action description for error message
 * @param {Function} toast - Toast notification function
 * @returns {string|null} - User ID if valid, null if invalid
 */
export const validateUserAuth = (user, action, toast) => {
  const userId = getUserId(user);
  
  if (!userId) {
    showAuthRequiredMessage(action, toast);
    return null;
  }
  
  return userId;
};

const authUtils = {
  getUserId,
  getUserEmail,
  isAuthenticated,
  isAdmin,
  getUserDisplayName,
  getUserRank,
  showAuthRequiredMessage,
  createUserDataForAPI,
  validateUserAuth
};

export default authUtils;
